/* ============================================================
   global.js — Web Components: details-disclosure, modal-dialog,
   quantity-input, predictive-search, sticky header, mobile drawer
   ============================================================ */

/* Helper: format money */
function formatMoney(cents, format) {
  const fmt = format || window.theme?.moneyFormat || '${{amount}}';
  const value = (cents / 100).toFixed(2);
  return fmt.replace(/\{\{\s*amount\s*\}\}/g, value);
}
window.formatMoney = formatMoney;

/* Sticky header */
const header = document.querySelector('.site-header');
if (header) {
  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    header.classList.toggle('scrolled', y > 20);
    lastY = y;
  }, { passive: true });
}

/* Mobile drawer */
class MobileDrawer extends HTMLElement {
  connectedCallback() {
    this.toggle = document.querySelector('[data-mobile-toggle]');
    this.closeBtn = this.querySelector('[data-drawer-close]');
    if (this.toggle) this.toggle.addEventListener('click', () => this.open());
    if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
  }
  open() {
    this.setAttribute('open', '');
    document.body.style.overflow = 'hidden';
  }
  close() {
    this.removeAttribute('open');
    document.body.style.overflow = '';
  }
}
customElements.define('mobile-drawer', MobileDrawer);

/* Quantity input */
class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input');
    this.querySelectorAll('button').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const change = btn.dataset.action === 'increment' ? 1 : -1;
        const min = parseInt(this.input.min || '1', 10);
        const max = parseInt(this.input.max || '999', 10);
        const next = Math.min(max, Math.max(min, parseInt(this.input.value, 10) + change));
        if (next !== parseInt(this.input.value, 10)) {
          this.input.value = next;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }
}
customElements.define('quantity-input', QuantityInput);

/* Generic modal dialog */
class ModalDialog extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('[data-modal-close]').forEach((b) =>
      b.addEventListener('click', () => this.close())
    );
    this.addEventListener('click', (e) => {
      if (e.target === this) this.close();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    });
  }
  open() { this.setAttribute('open', ''); document.body.style.overflow = 'hidden'; }
  close() { this.removeAttribute('open'); document.body.style.overflow = ''; }
}
customElements.define('modal-dialog', ModalDialog);

/* Predictive search */
class PredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('.predictive-search');
    if (!this.input || !this.results) return;
    let timer;
    this.input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = this.input.value.trim();
      if (q.length < 2) { this.results.removeAttribute('open'); return; }
      timer = setTimeout(() => this.fetchResults(q), 200);
    });
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.results.removeAttribute('open');
    });
  }
  async fetchResults(q) {
    try {
      const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
      const res = await fetch(url);
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.predictive-search__inner');
      if (inner) {
        this.results.innerHTML = inner.innerHTML;
        this.results.setAttribute('open', '');
      }
    } catch (err) { console.error('Predictive search failed', err); }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/* Search overlay toggle */
document.querySelectorAll('[data-open-search]').forEach((btn) => {
  btn.addEventListener('click', () => {
    const overlay = document.getElementById('SearchOverlay');
    if (overlay) {
      overlay.setAttribute('open', '');
      overlay.querySelector('input')?.focus();
    }
  });
});
document.querySelectorAll('[data-close-search]').forEach((btn) => {
  btn.addEventListener('click', () => {
    document.getElementById('SearchOverlay')?.removeAttribute('open');
  });
});

/* Tabs */
document.querySelectorAll('[data-tabs]').forEach((tabs) => {
  const btns = tabs.querySelectorAll('.tabs__btn');
  const panels = tabs.querySelectorAll('.tabs__panel');
  btns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.target;
      btns.forEach((b) => b.classList.toggle('is-active', b === btn));
      panels.forEach((p) => p.classList.toggle('is-active', p.id === target));
    });
  });
});

/* Lazy-reveal sections via IntersectionObserver */
const io = new IntersectionObserver(
  (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        io.unobserve(entry.target);
      }
    });
  },
  { rootMargin: '0px 0px -80px 0px' }
);
document.querySelectorAll('[data-reveal]').forEach((el) => io.observe(el));

/* Newsletter popup (one-time, 8s) */
(function () {
  if (sessionStorage.getItem('tsb-newsletter-seen')) return;
  const popup = document.getElementById('NewsletterPopup');
  if (!popup) return;
  setTimeout(() => {
    popup.setAttribute('open', '');
    sessionStorage.setItem('tsb-newsletter-seen', '1');
  }, 8000);
})();

/* Wishlist (localStorage) */
window.Wishlist = {
  key: 'tsb-wishlist',
  get() { try { return JSON.parse(localStorage.getItem(this.key)) || []; } catch (e) { return []; } },
  has(id) { return this.get().includes(String(id)); },
  set(list) {
    localStorage.setItem(this.key, JSON.stringify(list));
    document.dispatchEvent(new CustomEvent('wishlist:update'));
  },
  add(id) {
    const list = this.get();
    const sid = String(id);
    if (!list.includes(sid)) list.push(sid);
    this.set(list);
    return true;
  },
  remove(id) {
    const list = this.get();
    const sid = String(id);
    const i = list.indexOf(sid);
    if (i >= 0) list.splice(i, 1);
    this.set(list);
    return false;
  },
  toggle(id) {
    const list = this.get();
    const sid = String(id);
    const i = list.indexOf(sid);
    if (i >= 0) list.splice(i, 1); else list.push(sid);
    this.set(list);
    return list.includes(sid);
  },
  clear() { this.set([]); },
};
// Sync across tabs/windows
window.addEventListener('storage', (e) => {
  if (e.key === window.Wishlist.key) {
    document.dispatchEvent(new CustomEvent('wishlist:update'));
  }
});
function tsbToast(msg) {
  let t = document.getElementById('tsb-toast');
  if (!t) {
    t = document.createElement('div');
    t.id = 'tsb-toast';
    t.style.cssText = 'position:fixed;left:50%;bottom:32px;transform:translateX(-50%) translateY(20px);background:#1a1a1a;color:#fff;padding:12px 20px;border-radius:999px;font-size:14px;font-weight:500;z-index:9999;opacity:0;transition:all .25s ease;box-shadow:0 8px 24px rgba(0,0,0,.15);pointer-events:none';
    document.body.appendChild(t);
  }
  t.textContent = msg;
  requestAnimationFrame(() => { t.style.opacity = '1'; t.style.transform = 'translateX(-50%) translateY(0)'; });
  clearTimeout(t._timer);
  t._timer = setTimeout(() => { t.style.opacity = '0'; t.style.transform = 'translateX(-50%) translateY(20px)'; }, 2200);
}
window.tsbToast = tsbToast;

function tsbSyncWishlistButtons() {
  const ids = window.Wishlist.get();
  document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
    const active = ids.includes(String(btn.dataset.productId));
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  });
  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = ids.length;
    // Hide the badge when empty (works for both header pills and inline counters)
    if (el.classList.contains('cart-bubble') || el.tagName === 'SPAN' && el.closest('a')) {
      el.style.display = ids.length > 0 ? '' : 'none';
    }
  });
}
// Run immediately so the header badge updates on every page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', tsbSyncWishlistButtons);
} else {
  tsbSyncWishlistButtons();
}
document.addEventListener('wishlist:update', tsbSyncWishlistButtons);

document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-wishlist-toggle]');
  if (!btn) return;
  e.preventDefault();
  const id = btn.dataset.productId;
  if (!id) return;
  const active = window.Wishlist.toggle(id);
  btn.classList.toggle('is-active', active);
  btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  tsbToast(active ? 'Added to wishlist' : 'Removed from wishlist');
});

/* Recently viewed (localStorage, on product page) */
(function () {
  const product = document.querySelector('[data-product-id]');
  if (!product) return;
  const id = product.dataset.productId;
  const key = 'tsb-recently-viewed';
  let list = [];
  try { list = JSON.parse(localStorage.getItem(key)) || []; } catch (e) {}
  list = [id, ...list.filter((x) => x !== id)].slice(0, 6);
  localStorage.setItem(key, JSON.stringify(list));
})();

/* ============================================================
   <testimonial-slider> Web Component
   ============================================================ */
class TestimonialSlider extends HTMLElement {
  constructor() { super(); this.index = 0; this.autoplayTimer = null; }
  connectedCallback() {
    this.track = this.querySelector('[data-testimonial-track]');
    this.viewport = this.querySelector('[data-testimonial-viewport]');
    this.dotsHost = this.querySelector('[data-testimonial-dots]');
    this.slides = this.track ? Array.from(this.track.children) : [];
    if (!this.track || !this.slides.length) return;

    this.prev = this.querySelector('.testimonial-slider__arrow--prev');
    this.next = this.querySelector('.testimonial-slider__arrow--next');

    this.prev && this.prev.addEventListener('click', () => this.go(this.index - 1));
    this.next && this.next.addEventListener('click', () => this.go(this.index + 1));

    this.computePages();
    this.renderDots();
    this.update();

    // Recompute on resize
    this._onResize = () => { this.computePages(); this.renderDots(); this.update(); };
    window.addEventListener('resize', this._onResize, { passive: true });

    // Autoplay
    if (this.getAttribute('data-autoplay') === 'true') {
      const interval = parseInt(this.getAttribute('data-interval'), 10) || 6000;
      this.startAutoplay(interval);
      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => this.startAutoplay(interval));
    }

    // Swipe (touch)
    let startX = 0, currentX = 0, isDown = false;
    this.viewport.addEventListener('touchstart', (e) => { isDown = true; startX = e.touches[0].clientX; }, { passive: true });
    this.viewport.addEventListener('touchmove', (e) => { if (!isDown) return; currentX = e.touches[0].clientX; }, { passive: true });
    this.viewport.addEventListener('touchend', () => {
      if (!isDown) return; isDown = false;
      const diff = startX - currentX;
      if (Math.abs(diff) > 40) this.go(this.index + (diff > 0 ? 1 : -1));
    });
  }
  disconnectedCallback() {
    this.stopAutoplay();
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }
  computePages() {
    const w = window.innerWidth;
    this.perView = w < 640 ? 1 : w < 960 ? 2 : 3;
    this.pageCount = Math.max(1, this.slides.length - this.perView + 1);
    if (this.index > this.pageCount - 1) this.index = this.pageCount - 1;
    if (this.index < 0) this.index = 0;
  }
  go(i) {
    this.index = Math.max(0, Math.min(this.pageCount - 1, i));
    this.update();
  }
  update() {
    if (!this.slides.length) return;
    const slide = this.slides[0];
    const slideW = slide.getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0) || 20;
    const offset = -(this.index * (slideW + gap));
    this.track.style.transform = `translateX(${offset}px)`;

    if (this.prev) this.prev.classList.toggle('is-disabled', this.index === 0);
    if (this.next) this.next.classList.toggle('is-disabled', this.index >= this.pageCount - 1);

    if (this.dotsHost) {
      Array.from(this.dotsHost.children).forEach((dot, i) => dot.classList.toggle('is-active', i === this.index));
    }
  }
  renderDots() {
    if (!this.dotsHost) return;
    this.dotsHost.innerHTML = '';
    if (this.pageCount <= 1) return;
    for (let i = 0; i < this.pageCount; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'testimonial-slider__dot' + (i === this.index ? ' is-active' : '');
      b.setAttribute('aria-label', `Go to slide ${i + 1}`);
      b.addEventListener('click', () => this.go(i));
      this.dotsHost.appendChild(b);
    }
  }
  startAutoplay(interval) {
    this.stopAutoplay();
    this.autoplayTimer = setInterval(() => {
      this.go(this.index >= this.pageCount - 1 ? 0 : this.index + 1);
    }, interval);
  }
  stopAutoplay() {
    if (this.autoplayTimer) { clearInterval(this.autoplayTimer); this.autoplayTimer = null; }
  }
}
customElements.define('testimonial-slider', TestimonialSlider);

/* ============================================================
   Star rating input — fill stars 1..N when one is selected
   ============================================================ */
document.addEventListener('click', (e) => {
  const star = e.target.closest('.rating-input__star');
  if (!star) return;
  const group = star.closest('.rating-input');
  if (!group) return;
  const stars = Array.from(group.querySelectorAll('.rating-input__star'));
  const idx = stars.indexOf(star);
  stars.forEach((s, i) => {
    const span = s.querySelector('span');
    if (span) span.style.color = i <= idx ? '#f59e0b' : '#d4d4d4';
  });
});
// Initial state on load
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.rating-input').forEach((group) => {
    const stars = Array.from(group.querySelectorAll('.rating-input__star'));
    const checked = stars.findIndex((s) => s.querySelector('input:checked'));
    if (checked >= 0) {
      stars.forEach((s, i) => {
        const span = s.querySelector('span');
        if (span) span.style.color = i <= checked ? '#f59e0b' : '#d4d4d4';
      });
    }
  });
});

/* ============================================================
   Product Gallery — smooth crossfade between images
   Click thumb → swap main image with fade transition.
   Also handles keyboard arrow nav + click-to-zoom modal.
   ============================================================ */
(function () {
  function initGallery(gallery) {
    if (!gallery || gallery.__inited) return;
    gallery.__inited = true;

    const thumbs = Array.from(gallery.querySelectorAll('.product-gallery__thumb'));
    const main = gallery.querySelector('.product-gallery__main');
    if (!main || thumbs.length === 0) return;

    let mainImg = main.querySelector('img');
    let isAnimating = false;

    function swapTo(url, alt) {
      if (!url || isAnimating) return;
      if (mainImg && mainImg.src === url) return;
      isAnimating = true;

      // Preload the next image to avoid a flash of nothing
      const next = new Image();
      next.decoding = 'async';
      next.onload = function () {
        const newImg = document.createElement('img');
        newImg.src = url;
        newImg.alt = alt || (mainImg ? mainImg.alt : '');
        newImg.loading = 'eager';
        newImg.fetchpriority = 'high';
        newImg.classList.add('is-entering');
        main.appendChild(newImg);

        // Fade out old, fade in new
        requestAnimationFrame(() => {
          if (mainImg) mainImg.classList.add('is-leaving');
          requestAnimationFrame(() => {
            newImg.classList.remove('is-entering');
          });
        });

        setTimeout(() => {
          if (mainImg && mainImg.parentNode === main) mainImg.remove();
          mainImg = newImg;
          isAnimating = false;
        }, 380);
      };
      next.onerror = function () { isAnimating = false; };
      next.src = url;
    }

    function setActive(thumb) {
      thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
    }

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const url = thumb.getAttribute('data-full');
        const img = thumb.querySelector('img');
        const alt = img ? img.alt : '';
        setActive(thumb);
        swapTo(url, alt);
      });
      thumb.addEventListener('mouseenter', () => {
        // Preload on hover for instant response
        const url = thumb.getAttribute('data-full');
        if (url) { const pre = new Image(); pre.src = url; }
      });
    });

    // Keyboard nav (left/right) when the gallery has focus
    gallery.tabIndex = 0;
    gallery.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const active = thumbs.findIndex((t) => t.classList.contains('is-active'));
      const idx = active < 0 ? 0 : active;
      const next = e.key === 'ArrowRight' ? (idx + 1) % thumbs.length : (idx - 1 + thumbs.length) % thumbs.length;
      thumbs[next].click();
      thumbs[next].scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      e.preventDefault();
    });

    // Click main image → lightbox zoom
    main.addEventListener('click', () => {
      if (!mainImg || !mainImg.src) return;
      openLightbox(mainImg.src, mainImg.alt);
    });
  }

  function openLightbox(url, alt) {
    let lb = document.querySelector('.tsb-lightbox');
    if (!lb) {
      lb = document.createElement('div');
      lb.className = 'tsb-lightbox';
      lb.innerHTML = '<button class="tsb-lightbox__close" aria-label="Close">×</button><img alt="">';
      document.body.appendChild(lb);
      lb.addEventListener('click', (e) => {
        if (e.target === lb || e.target.classList.contains('tsb-lightbox__close')) {
          lb.classList.remove('is-open');
        }
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') lb.classList.remove('is-open');
      });
    }
    const img = lb.querySelector('img');
    img.src = url;
    img.alt = alt || '';
    requestAnimationFrame(() => lb.classList.add('is-open'));
  }

  function initAll() {
    document.querySelectorAll('.product-gallery').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  // Re-init after section reloads in Theme Editor
  document.addEventListener('shopify:section:load', initAll);
})();

/* ============================================================
   Sticky header height — expose as --header-height CSS var
   So sticky offsets (e.g. product gallery) line up exactly.
   ============================================================ */
(function () {
  function setHeaderHeight() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const h = header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', h + 'px');
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setHeaderHeight);
  } else {
    setHeaderHeight();
  }
  window.addEventListener('resize', setHeaderHeight);
  window.addEventListener('load', setHeaderHeight);
  document.addEventListener('shopify:section:load', setHeaderHeight);
  document.addEventListener('shopify:section:unload', setHeaderHeight);
})();
